import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Copy, Check, Loader2, TriangleAlert, RefreshCw, Film, Music } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Empty } from '../../components/states.jsx';
import { formatDate } from '../../lib/date.js';
import { formatSize, formatLength } from '../../lib/media.js';

const POLL_MS = 2500;

const isBusy = (file) => file.status === 'queued' || file.status === 'processing';

/** Řádek se stavem převodu — u záznamů je to to hlavní, co chcete vidět. */
function Status({ file }) {
  if (file.status === 'processing') {
    return (
      <span className="file-status is-working">
        <Loader2 size={13} className="spin" aria-hidden="true" />
        Převádím na MP4 {file.progress ? `· ${file.progress} %` : ''}
      </span>
    );
  }
  if (file.status === 'queued') {
    return <span className="file-status is-working"><Loader2 size={13} className="spin" aria-hidden="true" /> Čeká ve frontě</span>;
  }
  if (file.status === 'failed') {
    return (
      <span className="file-status is-failed" title={file.error || ''}>
        <TriangleAlert size={13} aria-hidden="true" />
        Převod selhal — soubor zůstal v původní podobě
      </span>
    );
  }
  if (file.kind !== 'file') {
    const length = formatLength(file.duration);
    return (
      <span className="file-status">
        {file.kind === 'video' ? <Film size={13} aria-hidden="true" /> : <Music size={13} aria-hidden="true" />}
        Připraveno k přehrání{length ? ` · ${length}` : ''}
      </span>
    );
  }
  return null;
}

export default function FilesAdmin({ files, notify, reloadFiles }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [copied, setCopied] = useState(null);
  const [limits, setLimits] = useState(null);
  const [converting, setConverting] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { reloadFiles(); }, [reloadFiles]);
  useEffect(() => { api.admin.fileLimits().then(setLimits).catch(() => {}); }, []);

  // Dokud se něco převádí, průběh si doptáváme — server nemá jak dát vědět sám.
  const working = files.some(isBusy);
  useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(() => { reloadFiles(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [working, reloadFiles]);

  const upload = useCallback(async (list) => {
    const selected = Array.from(list || []);
    if (!selected.length) return;

    const form = new FormData();
    selected.forEach((file) => form.append('files', file));

    setBusy(true);
    setProgress(0);
    try {
      const saved = await api.admin.upload(form, setProgress);
      await reloadFiles();
      const count = saved.length;
      const queued = saved.filter(isBusy).length;
      notify(
        `Nahráno ${count} ${count === 1 ? 'soubor' : count < 5 ? 'soubory' : 'souborů'}.`
        + (queued ? ` ${queued === 1 ? 'Záznam se převádí' : 'Záznamy se převádějí'} na MP4 — vydržte.` : ''),
      );
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [notify, reloadFiles]);

  const remove = async (file) => {
    if (!window.confirm(`Smazat „${file.originalName}"?\n\nOdkazy na tento soubor v hodinách a úkolech přestanou fungovat.`)) return;
    try {
      await api.admin.deleteFile(file.id);
      await reloadFiles();
      notify('Soubor smazán.');
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  const convert = async (file) => {
    setConverting(file.id);
    try {
      await api.admin.convertFile(file.id);
      await reloadFiles();
      notify('Převod spuštěn.');
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setConverting(null);
    }
  };

  const copy = async (file) => {
    try {
      await navigator.clipboard.writeText(file.url);
      setCopied(file.id);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      notify('Kopírování se nezdařilo, zkopírujte adresu ručně.', 'error');
    }
  };

  const percent = Math.round(progress * 100);

  return (
    <>
      <div className="admin-title">
        <div>
          <h1>Soubory</h1>
          <p>Slajdy, šablony, data — a záznamy hodin. Odkazy pak vybíráte přes sponku u hodin a úkolů.</p>
        </div>
      </div>

      <div
        className={`dropzone${dragging ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (busy) return;
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); inputRef.current?.click(); }
        }}
        onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => { event.preventDefault(); setDragging(false); if (!busy) upload(event.dataTransfer.files); }}
        role="button"
        tabIndex={0}
        aria-label="Nahrát soubory"
        aria-busy={busy}
      >
        <Upload size={22} aria-hidden="true" />
        <strong>{busy ? `Nahrávám… ${percent} %` : 'Přetáhněte soubory sem nebo klepněte'}</strong>

        {busy ? (
          <div className="upload-bar"><div style={{ width: `${percent}%` }} /></div>
        ) : (
          <small>
            {limits
              ? `PDF, Python, obrázky a další do ${formatSize(limits.maxBytes)} · záznamy hodin (MP4, MKV, MOV…) do ${formatSize(limits.mediaMaxBytes)}`
              : 'PDF, Python, CSV, obrázky, záznamy hodin'}
          </small>
        )}

        {busy && <small>Zavřením stránky se nahrávání přeruší.</small>}
        {!busy && limits && !limits.canConvert && (
          <small className="upload-warn">
            Pozor: na serveru chybí ffmpeg, záznamy se nepřevedou — nahrávejte rovnou MP4.
          </small>
        )}

        <input ref={inputRef} type="file" multiple hidden onChange={(event) => upload(event.target.files)} />
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        {files.length === 0 ? (
          <Empty title="Zatím nic nahraného">Nahrané soubory se objeví tady.</Empty>
        ) : (
          <div className="stack-tight">
            {files.map((file) => (
              <div className="admin-row" key={file.id}>
                {file.poster && <img className="file-thumb" src={file.poster} alt="" loading="lazy" />}

                <div className="admin-row-main">
                  <div className="admin-row-title">{file.originalName}</div>
                  <div className="admin-row-sub">
                    <span>{formatSize(file.size)}</span>
                    <span>{formatDate(file.createdAt.slice(0, 10))}</span>
                    <span className="file-url">{file.url}</span>
                  </div>
                  <Status file={file} />
                  {file.status === 'processing' && (
                    <div className="upload-bar upload-bar-slim"><div style={{ width: `${file.progress}%` }} /></div>
                  )}
                </div>

                <div className="admin-row-actions">
                  {file.convertible && !isBusy(file) && (
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => convert(file)}
                      disabled={converting === file.id}
                      aria-label={`Převést ${file.originalName} na MP4`}
                      title="Převést na MP4"
                    >
                      <RefreshCw size={15} className={converting === file.id ? 'spin' : undefined} />
                    </button>
                  )}
                  <button type="button" className="icon-btn" onClick={() => copy(file)} aria-label={`Zkopírovat odkaz na ${file.originalName}`}>
                    {copied === file.id ? <Check size={15} style={{ color: 'var(--ok)' }} /> : <Copy size={15} />}
                  </button>
                  <a href={file.url} target="_blank" rel="noopener noreferrer" className="icon-btn" aria-label={`Otevřít ${file.originalName}`}>
                    <Upload size={15} style={{ transform: 'rotate(180deg)' }} />
                  </a>
                  <button type="button" className="icon-btn icon-btn-danger" onClick={() => remove(file)} aria-label={`Smazat ${file.originalName}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
